(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const initProducts = (root) => {
    if (root.dataset.nanoProductsReady === 'true') return;
    root.dataset.nanoProductsReady = 'true';

    const viewport = root.querySelector('[data-nano-products-grid-viewport]');
    const grid = root.querySelector('[data-nano-products-grid]');
    if (!viewport || !grid) return;

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    let minX = 0;
    let currentX = 0;
    let resizeTimer = null;

    const dragState = {
      pointerId: null,
      startX: 0,
      startTranslate: 0,
      dragging: false,
      suppressClick: false,
    };

    const openCartDrawer = () => {
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        document.getElementById('cart-drawer-toggle')?.click();
      }
    };

    const refreshCart = () => {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    };

    const dispatchAddToCartTracking = (button, product) => {
      document.dispatchEvent(new CustomEvent('ga4:add_to_cart', {
        detail: {
          button,
          product,
          quantity: 1,
        },
      }));
    };

    const addVariantToCart = async (button) => {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
          }),
        });

        const state = await response.json();
        if (!response.ok) {
          throw new Error(state.description || state.message || 'Unable to add item to cart.');
        }

        refreshCart();
        openCartDrawer();
        dispatchAddToCartTracking(button, state);
      } catch (error) {
        console.warn('[nano-products] add to cart failed', error);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    };

    const clampX = (x) => Math.max(minX, Math.min(0, x));

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === 1) return event.deltaX * 16;
      if (event.deltaMode === 2) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const setX = (x, { animate = false } = {}) => {
      if (!mediaQuery.matches) {
        currentX = 0;
        grid.style.transition = '';
        grid.style.transform = '';
        return;
      }

      currentX = clampX(x);
      grid.style.transition = animate ? 'transform 320ms ease' : 'none';
      grid.style.transform = `translate3d(${currentX}px, 0, 0)`;
    };

    const refresh = () => {
      if (!mediaQuery.matches) {
        setX(0);
        return;
      }

      const viewportStyle = window.getComputedStyle(viewport);
      const viewportPadding =
        Number.parseFloat(viewportStyle.paddingLeft) +
        Number.parseFloat(viewportStyle.paddingRight);
      const viewportWidth = viewport.clientWidth - viewportPadding;
      const overflow = grid.scrollWidth - viewportWidth;
      minX = overflow > 1 ? -overflow : 0;
      setX(minX === 0 ? 0 : currentX);
    };

    const clearSuppressClick = () => {
      dragState.suppressClick = false;
    };

    viewport.addEventListener('pointerdown', (event) => {
      if (!mediaQuery.matches || minX === 0) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      dragState.pointerId = event.pointerId;
      dragState.startX = event.clientX;
      dragState.startTranslate = currentX;
      dragState.dragging = false;
      dragState.suppressClick = false;
      grid.style.transition = 'none';
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (dragState.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - dragState.startX;
      if (!dragState.dragging && Math.abs(deltaX) > 6) {
        dragState.dragging = true;
        dragState.suppressClick = true;
      }

      if (!dragState.dragging) return;

      setX(dragState.startTranslate + deltaX);
      event.preventDefault();
    });

    const endDrag = (event) => {
      if (dragState.pointerId !== event.pointerId) return;

      if (viewport.hasPointerCapture?.(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }

      dragState.pointerId = null;
      if (dragState.dragging) {
        setX(currentX);
        window.setTimeout(clearSuppressClick, 80);
      } else {
        dragState.suppressClick = false;
      }
      dragState.dragging = false;
    };

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('lostpointercapture', () => {
      dragState.pointerId = null;
      dragState.dragging = false;
      window.setTimeout(clearSuppressClick, 80);
    });

    grid.addEventListener('click', (event) => {
      if (!dragState.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-nano-products-add]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      addVariantToCart(button);
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!mediaQuery.matches || minX === 0) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setX(currentX - deltaX);
      },
      { passive: false }
    );

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(refresh, 120);
    });

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', refresh);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(refresh);
    }

    grid.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', refresh, { once: true });
    });

    refresh();
    requestAnimationFrame(refresh);
    window.setTimeout(refresh, 80);
    document.fonts?.ready?.then(refresh).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-nano-products]').forEach(initProducts);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
